import {Platform} from 'react-native';

export type MobileOS = 'ios' | 'android' | 'other';

export interface BrowserInfo {
  name: string;
  icon: string;
}

/**
 * Detect if current environment is a mobile browser
 */
export const isMobileBrowser = (): boolean => {
  if (Platform.OS !== 'web') {
    return false;
  }

  const nav = window.navigator;

  const platform = (nav.platform || '').toLowerCase();
  const ua = (nav.userAgent || '').toLowerCase();

  // Desktop Signal
  const isDesktopPlatform = /macintel|win32|win64|linux x86_64|linux i686/.test(
    platform,
  );

  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;

  // If it's clearly desktop → immediately return false
  if (isDesktopPlatform) {
    return false;
  }

  // Mobile signals
  const uaMobile = /android|iphone|ipad|ipod|iemobile|opera mini|mobile/i.test(
    ua,
  );

  const uaDataMobile = (nav as any).userAgentData?.mobile === true;

  return uaMobile || uaDataMobile || coarsePointer;
};

/**
 * Detect mobile OS
 */
export const getMobileOS = (): MobileOS => {
  if (Platform.OS !== 'web') {
    return 'other';
  }

  const ua =
    window.navigator.userAgent ||
    window.navigator.vendor ||
    (window as any).opera ||
    '';

  const normalizedUA = ua.toLowerCase();

  if (/android/.test(normalizedUA)) {
    return 'android';
  }

  if (/iphone|ipad|ipod/.test(normalizedUA)) {
    return 'ios';
  }

  return 'other';
};

/**
 * Detect browser information
 */
export const getBrowserInfo = (): BrowserInfo => {
  if (Platform.OS !== 'web') {
    return {
      name: 'App',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/archive/globe_128x128.png',
    };
  }

  const ua =
    window.navigator.userAgent ||
    window.navigator.vendor ||
    (window as any).opera ||
    '';

  const normalizedUA = ua.toLowerCase();

  /**
   * FIREFOX
   * Desktop Firefox => Firefox
   * iOS Firefox => FxiOS
   */
  const isFirefox =
    /firefox|fxios/.test(normalizedUA) ||
    typeof (window as any).InstallTrigger !== 'undefined';

  if (isFirefox) {
    return {
      name: 'Firefox',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/firefox/firefox_128x128.png',
    };
  }

  /**
   * EDGE
   */
  const isEdge = /edg|edgios|edga/.test(normalizedUA);

  if (isEdge) {
    return {
      name: 'Edge',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/edge/edge_128x128.png',
    };
  }

  /**
   * SAMSUNG INTERNET
   */
  const isSamsung = /samsungbrowser/.test(normalizedUA);

  if (isSamsung) {
    return {
      name: 'Samsung Browser',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/samsung-internet/samsung-internet_128x128.png',
    };
  }

  /**
   * OPERA
   */
  const isOpera = /opr\/|opera|opios/.test(normalizedUA);

  if (isOpera) {
    return {
      name: 'Opera',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/opera/opera_128x128.png',
    };
  }

  /**
   * CHROME
   * Exclude Edge / Opera / Samsung
   */
  const isChrome =
    /chrome|crios/.test(normalizedUA) &&
    !/edg|opr\/|opera|samsungbrowser/.test(normalizedUA);

  if (isChrome) {
    return {
      name: 'Chrome',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/chrome/chrome_128x128.png',
    };
  }

  /**
   * SAFARI
   * Keep last because all iOS browsers contain Safari
   */
  const isSafari =
    /safari/.test(normalizedUA) &&
    !/chrome|crios|fxios|edg|opr\//.test(normalizedUA);

  if (isSafari) {
    return {
      name: 'Safari',
      icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/safari/safari_128x128.png',
    };
  }

  /**
   * FALLBACK
   */
  return {
    name: 'Mobile Browser',
    icon: 'https://cdnjs.cloudflare.com/ajax/libs/browser-logos/70.4.0/archive/globe_128x128.png',
  };
};
